import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { PERMISSIONS } from "../../utils/permissions";
import { FieldGroup, FormArray, FormBuilder } from "react-reactive-form";
import PDQuestions from "../../questions/PDQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import {
  populateValueHelperArray,
  // extractArrayValues,
} from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";
import { WIZARD_SECTIONS } from "../../utils/wizardSections";
import {
  getRestorableFormArray,
  getRestoreArrayType,
  toRestorableArray,
} from "../../utils/wizardFormRestore";

const isProduction = process.env.NODE_ENV === "production";

const Pharmacodynamics = () => {
  const { studyId } = useParams();
  const { setAjaxLoading } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    loadWizardSections,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
    updatePdTreatmentGroupsComparisonAdditionalData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  // const formArraysKeysToClean = ["Metabolites"];

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAjaxLoading(true);
    loadWizardSections([
      WIZARD_SECTIONS.standardPd,
      WIZARD_SECTIONS.comparativePd,
    ])
      .then(() => {
        setErrMsg("");
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to get questions");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  }, [studyId, loadWizardSections, setAjaxLoading]);

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      const hasFormContent = Object.keys(wizardForm.value).some(key => {
        const value = wizardForm.value[key];
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== null && value !== "" && value !== undefined;
      });

      if (hasFormContent && wizardForm.dirty) {
        return;
      }

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (
          !getRestoreArrayType(
            wizardForm,
            key,
            formArraysKeys,
            wizardFormValue[key]
          )
        ) {
          return;
        }

        const control = getRestorableFormArray(wizardForm, key);
        while (control && control.length > 0) {
          control.removeAt(0);
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        const arrayType = getRestoreArrayType(
          wizardForm,
          key,
          formArraysKeys,
          wizardFormValue[key]
        );

        if (arrayType) {
          const control = getRestorableFormArray(wizardForm, key);
          const items = toRestorableArray(wizardFormValue[key], arrayType);

          items.forEach(item => {
            control?.push(
              arrayType === "object"
                ? FormBuilder.group(item)
                : FormBuilder.control(item)
            );
          });
        } else {
          // Ensure control exists before patching
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Extract qPdStdEnabled from root (new format) or inside qPdStdAnalyses (legacy)
      const stdPdEnabled =
        wizardFormValue.qPdStdEnabled ??
        wizardFormValue.qPdStdAnalyses?.qPdStdEnabled ??
        "false";
      if (!wizardForm.contains("qPdStdEnabled")) {
        wizardForm.addControl(
          "qPdStdEnabled",
          FormBuilder.control(stdPdEnabled)
        );
      } else {
        wizardForm.patchValue({ qPdStdEnabled: stdPdEnabled });
      }
      // Extract qPdCompEnabled from root (new format) or inside qPdCompAnalyses (legacy)
      const compPdEnabled =
        wizardFormValue.qPdCompEnabled ??
        wizardFormValue.qPdCompAnalyses?.qPdCompEnabled ??
        "false";
      if (!wizardForm.contains("qPdCompEnabled")) {
        wizardForm.addControl(
          "qPdCompEnabled",
          FormBuilder.control(compPdEnabled)
        );
      } else {
        wizardForm.patchValue({ qPdCompEnabled: compPdEnabled });
      }

      // Ensure qComparative control exists
      if (!wizardForm.contains("qComparative")) {
        wizardForm.addControl(
          "qComparative",
          FormBuilder.control(wizardFormValue.qComparative || "false")
        );
      }

      // Restore Standard PD blocks from qPdStdAnalyses (flat array or legacy object) or qPdAnalyses (legacy)
      const stdAnalyses = wizardFormValue.qPdStdAnalyses;
      const stdPdBlocks = Array.isArray(stdAnalyses)
        ? stdAnalyses
        : (stdAnalyses?.blocks ??
          (Array.isArray(wizardFormValue.qPdAnalyses)
            ? wizardFormValue.qPdAnalyses
            : []));
      if (stdPdBlocks.length > 0) {
        stdPdBlocks.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";

          // Restore all PD fields
          if (block.qPdTable !== undefined && block.qPdTable !== null) {
            const controlName = `qPdTable${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdTable);
          }

          if (
            block.qPdPopulation !== undefined &&
            block.qPdPopulation !== null
          ) {
            const controlName = `qPdPopulation${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdPopulation);
          }

          if (
            block.qPdComparative !== undefined &&
            block.qPdComparative !== null
          ) {
            const controlName = `qPdComparative${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPdComparative));
          }

          if (block.qPdCompName !== undefined && block.qPdCompName !== null) {
            const controlName = `qPdCompName${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdCompName);
          }

          if (
            block.qPdSummaryType !== undefined &&
            block.qPdSummaryType !== null
          ) {
            const controlName = `qPdSummaryType${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPdSummaryType));
          }

          // Restore qPdParameters array
          const qPdParametersControlName = `qPdParameters${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPdParametersControlName,
            block.qPdParameters
          );

          // Restore qPdTreatmentGroups array
          const qPdTreatmentGroupsControlName = `qPdTreatmentGroups${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPdTreatmentGroupsControlName,
            block.qPdTreatmentGroups
          );

          // Restore qPdComparisonPairs array
          const qPdComparisonPairsControlName = `qPdComparisonPairs${suffix}`;
          if (
            block.qPdComparisonPairs &&
            Array.isArray(block.qPdComparisonPairs) &&
            block.qPdComparisonPairs.length > 0
          ) {
            if (!wizardForm.contains(qPdComparisonPairsControlName)) {
              wizardForm.addControl(
                qPdComparisonPairsControlName,
                new FormArray([])
              );
            }
            const comparisonPairsArray = wizardForm.get(
              qPdComparisonPairsControlName
            );
            while (comparisonPairsArray.length > 0) {
              comparisonPairsArray.removeAt(0);
            }
            block.qPdComparisonPairs.forEach(pair => {
              const group = FormBuilder.group({
                Treatment: [pair.Treatment || ""],
                Comparator: [pair.Comparator || ""],
              });
              comparisonPairsArray.push(group);
            });
          }

          if (
            block.qPdDoseInteraction !== undefined &&
            block.qPdDoseInteraction !== null
          ) {
            const controlName = `qPdDoseInteraction${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm
              .get(controlName)
              .setValue(String(block.qPdDoseInteraction));
          }

          // Restore qPdColumnHeaders array
          const qPdColumnHeadersControlName = `qPdColumnHeaders${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPdColumnHeadersControlName,
            block.qPdColumnHeaders
          );

          // Restore qPdTimepoints array
          const qPdTimepointsControlName = `qPdTimepoints${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPdTimepointsControlName,
            block.qPdTimepoints
          );

          // Restore qPdStatTables
          if (
            block.qPdStatTables !== undefined &&
            block.qPdStatTables !== null
          ) {
            const controlName = `qPdStatTables${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            const statTablesValue =
              Array.isArray(block.qPdStatTables) &&
              block.qPdStatTables.length > 0
                ? block.qPdStatTables
                : typeof block.qPdStatTables === "string"
                  ? block.qPdStatTables
                  : "";
            wizardForm.get(controlName).setValue(statTablesValue);
          }
        });
      }
    }
  };

  // function cleanObject(obj) {
  //   for (const key in obj) {
  //     if (obj[key] === null || obj[key] === "") {
  //       return true;
  //     } else if (typeof obj[key] === "object" && cleanObject(obj[key])) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  // Use unified submit handler that collects data from all four pages
  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    sections: [WIZARD_SECTIONS.standardPd, WIZARD_SECTIONS.comparativePd],
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setIsSaving,
    questionsRef: null, // PD page doesn't use questionsRef
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {/* <div className={"questions-content__item"}>
              <div className={"questions-content__label intro-text"}>
                <span className={"questions-content__label-text"}>
                  Please answer all of the questions.
                </span>
              </div>
            </div> */}
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <PDQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      questionsData={questionsData.current}
                      updatePdTreatmentGroupsComparisonAdditionalData={
                        updatePdTreatmentGroupsComparisonAdditionalData
                      }
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Answer the following questions related to Pharmacodynamics"}
      headerButton={false}
      pageContent={PageContent()}
      // prevButton={{
      //   buttonTitle: "Previous",
      //   buttonGoToAction:
      //     wizardForm.controls.qPkCompEnabled?.value === "true"
      //       ? "/csr/" + studyId + "/comppharmacokinetics"
      //       : wizardForm.controls.qPkStdEnabled?.value === "true"
      //         ? "/csr/" + studyId + "/stdpharmacokinetics"
      //         : "/csr/" + studyId + "/questions",
      // }}
      prevButton={{
        buttonTitle: "Bioavailability",
        buttonGoToAction: "/csr/" + studyId + "/bioavailability",
      }}
      nextButton={{
        buttonTitle: "Data Mapping",
        buttonGoToAction: "/csr/" + studyId + "/data-mapping",
      }}
      saveButtonHandler={handleSubmit}
      savePermission={PERMISSIONS.csrWizardsUpdate}
      saveButton={true}
    />
  );
};

export default Pharmacodynamics;
